import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useClientInsurersByIdsLazy } from '../../../hooks/client-insurer-hook';
import { useFilterUrlSync } from '../../../hooks/use-filter-url-sync';
import {
  InsurerType,
  UnbilledBillsPaginatedType,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetBillingTypesQuery,
  useGetContractIdsLazyQuery,
  useGetContractsV2LazyQuery,
  useGetPartnershipsQuery,
  useGetPaymentTypesQuery,
  useGetUnbilledLazyQuery,
} from '../../../types';
import {
  BILL_DATE_FORMAT,
  UnbilledTypeDataList,
} from '../bill-grid-components/bill-management-types';
import { generateEnrichedData } from '../utils';
import { UnbilledFilters } from './unbilled-filters';
import { UnbilledGrid } from './unbilled-grid';

export interface UnbilledFilterModelType {
  endDate?: string;
  startDate: string;
  clientIds: number[];
  insurerIds: number[];
  partnershipIds: number[];
  billingTypes: string[];
  paymentTypes: string[];
}

export const default90DayLookbackStart = dayjs().subtract(90, 'days').toDate();
export const today = dayjs().toDate();

const emptyUnbilledData: UnbilledBillsPaginatedType = {
  items: [],
  page: 1,
  size: 0,
};

const defaultFilters: UnbilledFilterModelType = {
  endDate: dayjs(today).format(BILL_DATE_FORMAT),
  startDate: dayjs(default90DayLookbackStart).format(BILL_DATE_FORMAT),
  clientIds: [],
  insurerIds: [],
  partnershipIds: [],
  billingTypes: [],
  paymentTypes: [],
};

export const UnbilledReport = (): JSX.Element => {
  const [filterParams, setFilterParams] =
    useFilterUrlSync<UnbilledFilterModelType>(defaultFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [gridRows, setGridRows] = useState<UnbilledTypeDataList>([]);

  const {
    data: clientsData,
    error: clientsError,
    loading: clientsLoading,
  } = useGetAllClientsQuery();
  const {
    data: insurersData,
    error: insurersError,
    loading: insurersLoading,
  } = useGetAllInsurersQuery();
  const [
    getContracts,
    { data: contractsData, loading: filterLoading, error: filterError },
  ] = useGetContractsV2LazyQuery();
  const [
    getContractIds,
    { loading: contractIdsLoading, error: contractIdsError },
  ] = useGetContractIdsLazyQuery();

  const { data: ciData, searchClientInsurerIds } = useClientInsurersByIdsLazy();
  const [getUnbilled, { loading: unbilledLoading, error: unbilledError }] =
    useGetUnbilledLazyQuery({
      fetchPolicy: 'cache-and-network',
    });

  const [unbilledResult, setUnbilledResult] =
    useState<UnbilledBillsPaginatedType>(emptyUnbilledData);
  const { data: partnershipsData, error: partnershipsError } =
    useGetPartnershipsQuery({
      variables: { type: 'billing' },
    });
  const { data: paymentTypesData, error: paymentTypesError } =
    useGetPaymentTypesQuery();

  const { data: billingTypesData, error: billingTypesError } =
    useGetBillingTypesQuery();

  const hasError =
    !!unbilledError ||
    !!clientsError ||
    !!filterError ||
    !!insurersError ||
    !!partnershipsError ||
    !!contractIdsError ||
    !!billingTypesError;

  const isLoading =
    unbilledLoading ||
    filterLoading ||
    clientsLoading ||
    insurersLoading ||
    contractIdsLoading;

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(1);
  };

  useEffect(() => {
    const fetchAndQueryUnbilled = async (): Promise<void> => {
      const fetchContractIds = async (
        clientIds: number[],
        insurerIds: number[],
        billingTypes: string[],
        partnershipIds: number[],
      ): Promise<number[]> => {
        const result = await getContractIds({
          variables: {
            clientIds,
            insurerIds,
            billingTypes: billingTypes.map(type => type.toLowerCase()),
            partnershipIds,
          },
        });
        return result.data?.getContractIds ?? [];
      };

      // Fetch contract IDs based on contract filters
      const hasContractFilters =
        filterParams.clientIds.length >= 1 ||
        filterParams.insurerIds.length >= 1 ||
        filterParams.billingTypes.length >= 1 ||
        filterParams.partnershipIds.length >= 1;

      const contractIds = hasContractFilters
        ? await fetchContractIds(
            filterParams.clientIds,
            filterParams.insurerIds,
            filterParams.billingTypes,
            filterParams.partnershipIds,
          )
        : [];
      if (hasContractFilters && contractIds.length === 0) {
        setUnbilledResult(emptyUnbilledData);
        return;
      }

      // Fetch unbilled with the resolved contract IDs and bill level filters
      const hasBillFilters = filterParams.paymentTypes.length >= 1;

      const data = await getUnbilled({
        variables: {
          page,
          size: pageSize,
          startDate: filterParams.startDate,
          endDate: filterParams.endDate,
          contractIds,
          ...(hasBillFilters && {
            paymentTypes: filterParams.paymentTypes,
          }),
        },
      });

      const unbilledBills = data.data?.getUnbilledBills ?? emptyUnbilledData;
      setUnbilledResult(unbilledBills);

      if (unbilledBills.items.length > 0) {
        const items = unbilledBills.items;
        const contractIdsFromItems = new Set(
          items.map(item => item.contractId),
        );

        // Fetch contracts by IDs; CI search fires once contracts arrive (separate effect below)
        if (contractIdsFromItems.size > 0) {
          getContracts({
            variables: {
              size: 100,
              page: 1,
              contractIds: Array.from(contractIdsFromItems),
            },
          });
        }
      }
    };

    fetchAndQueryUnbilled();
  }, [
    filterParams.clientIds,
    filterParams.insurerIds,
    filterParams.billingTypes,
    filterParams.partnershipIds,
    filterParams.startDate,
    filterParams.endDate,
    filterParams.paymentTypes,
    getContractIds,
    getUnbilled,
    getContracts,
    page,
    pageSize,
  ]);

  // Fire CI search once contracts are loaded so we resolve clientsInsurerId from the contract
  useEffect(() => {
    if (contractsData?.getContractsV2.items.length) {
      const ciIds = new Set(
        contractsData.getContractsV2.items.map(c => c.clientsInsurerId),
      );
      if (ciIds.size > 0) {
        searchClientInsurerIds(ciIds);
      }
    }
  }, [contractsData, searchClientInsurerIds]);

  // Enrich the rows when all data is present and set rows
  useEffect(() => {
    if (
      unbilledResult.items.length > 0 &&
      ciData &&
      partnershipsData?.getPartnerships &&
      contractsData?.getContractsV2
    ) {
      const gridRows = generateEnrichedData(
        unbilledResult.items,
        ciData,
        partnershipsData.getPartnerships,
        contractsData.getContractsV2.items,
      );
      setGridRows(gridRows);
    } else {
      setGridRows([]);
    }
  }, [unbilledResult, ciData, partnershipsData, contractsData]);

  return (
    <HHStack spacing={4} padding={4}>
      <HHTypography hhVariant="section-title">
        Unsubmitted Bills (Subscription Related)
      </HHTypography>

      <UnbilledFilters
        clientsError={clientsError}
        clientsData={clientsData?.getAllClients ?? []}
        insurersError={insurersError}
        insurersData={(insurersData?.getAllInsurers as InsurerType[]) ?? []}
        partnershipsError={partnershipsError}
        partnershipsData={partnershipsData?.getPartnerships ?? []}
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        default90DayLookbackStart={default90DayLookbackStart}
        today={today}
        setPage={setPage}
        paymentTypesData={paymentTypesData?.getPaymentTypes ?? []}
        paymentTypesError={paymentTypesError}
        loading={isLoading}
        billingTypesData={billingTypesData?.getBillingTypes ?? []}
      />
      <UnbilledGrid
        key={gridRows.length}
        page={unbilledResult.page ?? page}
        setPage={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        rows={gridRows}
        loading={isLoading}
        hasError={hasError}
        filterParams={filterParams}
      />
    </HHStack>
  );
};
